import { todayDateRangeInUserTimeZone } from '@/helpers';
import { cloneDeep } from 'lodash-es';
import type { DataTableHeader } from 'vuetify';
import { useBetFiltersValidation } from '@/composables/useBetFiltersValidation';
import { Currency, TradingType } from '@/constants';
import { createRequiredSportFilter, resetRequiredSportFilter } from '@/helpers/sportFilter';
import { TurnoverSelectionItem } from '@/models/turnoverSelection';
import { TurnoverSelectionStats } from '@/models/turnoverSelectionStats';
import { createTurnoverSelectionPageHeaders } from '@/pages/stats/turnover/selection/TurnoverSelectionPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import ErrorService from '@/services/errorService';
import { turnoverService } from '@/services/turnoverService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { currencyDictionary } from '@/stores/simpleDictionary/currency';
import { IPageOptions, ITurnoverDetailState, ITurnoverSelectionDetailStats, ITurnoverSelectionFilters } from '@/types';
import { ITurnoverSelectionStatsApi } from '@/types/turnoverSelectionStats';

const defaultSortBy = [{ key: 'date', order: 'asc' as const }];

function createTurnoverSelectionStoreSetup(storeId: string) {
    // --- state ---
    const defaultStats: ITurnoverSelectionStatsApi = {
        user_count: 0,
        settled_bet_count: 0,
        unsettled_bet_count: 0,
        bet_count: 0,
        stake_sum: 0,
        balance: 0,
        net_payout_sum: 0,
        odds_avg: null,
    };
    const { isHandicapValid, isCorrectScoreValid } = useBetFiltersValidation();
    const items = shallowRef<TurnoverSelectionItem[]>([]);
    const stats = shallowRef<TurnoverSelectionStats>(new TurnoverSelectionStats(defaultStats));
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();
    const detailState = ref<Record<string, ITurnoverDetailState<ITurnoverSelectionDetailStats>>>({});

    // --- filters ---
    const filters = reactive<ITurnoverSelectionFilters>({
        sportId: createRequiredSportFilter(),
        eventDate: {
            value: todayDateRangeInUserTimeZone(),
            title: 'turnoverSelection.gameDateFilter',
            allowSaveState: false,
            disableClear: true,
        },
        eventSearch: {
            value: null,
            title: 'common.search',
            allowSaveState: false,
        },
        betTradingTypeId: {
            value: TradingType.BetOrder,
            title: 'common.tradingType',
            allowSaveState: true,
        },
        userCurrencyId: {
            value: null,
            title: 'common.playerCurrency',
            allowSaveState: true,
        },
        displayedCurrencyId: {
            value: Currency.Brl,
            title: 'common.displayedCurrency',
            allowSaveState: true,
            disableClear: true,
        },
        betOpenStatus: {
            value: null,
            title: 'common.betStatus',
            allowSaveState: true,
        },
        betEventType: {
            value: null,
            title: 'common.marketType',
            allowSaveState: true,
        },
        betEventPeriodId: {
            value: null,
            title: 'bets.periodType',
            allowSaveState: true,
        },
        betSelectionId: {
            value: null,
            title: 'common.betType',
            allowSaveState: true,
        },
        betHandicap: {
            value: null,
            title: 'filters.handicap',
            allowSaveState: true,
        },
        betCorrectScore: {
            value: null,
            title: 'common.correctScore',
            allowSaveState: true,
        },
        betPlatformId: {
            value: null,
            title: 'bets.platform',
            allowSaveState: true,
        },
        customLeagueLimitsCategoryId: {
            value: null,
            title: 'pregame.leagueLimitsCategory',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        resetRequiredSportFilter(filters.sportId);
        filters.eventDate.value = todayDateRangeInUserTimeZone();
        filters.eventSearch.value = null;
        filters.betTradingTypeId.value = TradingType.BetOrder;
        filters.userCurrencyId.value = null;
        filters.displayedCurrencyId.value = Currency.Brl;
        filters.betOpenStatus.value = null;
        filters.betEventType.value = null;
        filters.betEventPeriodId.value = null;
        filters.betSelectionId.value = null;
        filters.betHandicap.value = null;
        filters.betCorrectScore.value = null;
        filters.betPlatformId.value = null;
        filters.customLeagueLimitsCategoryId.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createTurnoverSelectionPageHeaders(currencyDictionary, filters.displayedCurrencyId.value));

    const availableColumns = computed(() => headers.value.filter((item) => item.title));

    const hiddenColumns = ref<string[]>([]);

    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title))
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'turnoverSelection',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        if (!isHandicapValid(filters.betHandicap.value)) return;
        if (!isCorrectScoreValid(filters.betCorrectScore.value)) return;

        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const filtersToSend = cloneDeep(filters);
            if (filters.eventSearch.value) {
                // filtersToSend.eventDate = { ...filtersToSend.eventDate, value: null };
            }

            const params = {
                filters: filtersToSend,
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: [],
            };

            const response = await turnoverService.selection(params);
            items.value = response.data.rows.map((item) => new TurnoverSelectionItem(item));
            stats.value = new TurnoverSelectionStats(response.data.stats);
            total.value = response.data.total;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function getDetailState(item: TurnoverSelectionItem) {
        const key = item.uniqKey;
        detailState.value[key] ??= { loading: false, loaded: false, error: false, rows: [] };
        return detailState.value[key];
    }

    function getSelectionDetailFilters(item: TurnoverSelectionItem) {
        return {
            ...filters,
            eventCustomEventId: { value: item.customEventId },
            betEventType: { value: item.eventType },
            betEventPeriodId: { value: item.periodTypeId },
            betSelectionId: { value: [item.selectionId] },
            betHandicap: { value: item.handicap ? parseFloat(item.handicap) : null },
            'bet.correct_score': { value: item.correctScore },
        };
    }

    async function loadSelectionDetail(item: TurnoverSelectionItem) {
        const state = getDetailState(item);
        if (state.loading) return;

        state.error = false;
        state.loaded = false;
        state.rows = [];
        state.loading = true;

        try {
            const response = await turnoverService.selectionDetail({
                filters: getSelectionDetailFilters(item) as any,
                options: {
                    sortBy: options.value.sortBy,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: [],
                requestKey: `turnover-selection-detail-${item.uniqKey}`,
            });
            state.rows = Array.isArray((response.data as any).rows) ? ((response.data as any).rows as ITurnoverSelectionDetailStats[]) : [];
            state.loaded = true;
        } catch (error) {
            state.error = true;
            ErrorService.log(error);
        } finally {
            state.loading = false;
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        detailState.value = {};
        stats.value = new TurnoverSelectionStats(defaultStats);
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        detailState,
        stats,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        getDetailState,
        loadSelectionDetail,
        updateOptions,
        clearData,
    };
}

export function useTurnoverSelectionStore(storeId: string) {
    const useStore = defineStore(storeId, () => createTurnoverSelectionStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'turnoverSelection'),
    });
    return useStore();
}

export type TurnoverSelectionStoreSetup = ReturnType<typeof useTurnoverSelectionStore>;
